import {
  allFieldsToFormLayoutFields,
  type FormLayoutField,
  type FormLayoutPage,
} from '../../../../../forms'
import {
  ALWAYS_REQUIRED_SURVEY_FIELDS,
  INITIAL_MCP_GENERATION_FIELDS,
} from '../../mcp-required-fields'
import { AllElevatorSurveyFields } from '../fields'
import { type ElevatorSurveyPartialSchema, ElevatorSurveySchema } from '../form'
import type { ElevatorSurveyFormLayout } from '../types'

export const buildLayoutWithCommonSections = (
  pages: FormLayoutPage[],
): ElevatorSurveyFormLayout => {
  const pageFieldKeys = new Set<string>(
    pages.flatMap((page) =>
      page.contents.flatMap((item) =>
        item.type === 'field' ? [item.key] : item.fields.map((f) => f.key),
      ),
    ),
  )
  // A field is required iff it's always-required or it's an eMCP calculation
  // input that actually appears in this layout. Fields not in the layout can't
  // be collected and must not gate submission.
  const requiredKeys = new Set<string>([
    ...ALWAYS_REQUIRED_SURVEY_FIELDS,
    ...INITIAL_MCP_GENERATION_FIELDS.filter((key) => pageFieldKeys.has(key)),
  ])
  // Keeps the UI's required-indicator (red `*`) in sync with the schema.
  // Without this, every field shows as required because the registry
  // defaults to `required: true`.
  const withRequiredFlag = (field: FormLayoutField): FormLayoutField => ({
    ...field,
    required: requiredKeys.has(field.key),
  })
  const pagesWithRequiredFlags = pages.map((page) => ({
    ...page,
    contents: page.contents.map((item) =>
      item.type === 'field'
        ? withRequiredFlag(item)
        : { ...item, fields: item.fields.map(withRequiredFlag) },
    ),
  }))

  const stepsWithForwardBackwardLinks = pagesWithRequiredFlags.map(
    (page, index) => {
      return {
        ...page,
        forwardLink:
          index < pagesWithRequiredFlags.length - 1
            ? pagesWithRequiredFlags[index + 1].key
            : null,
        backwardLink: index > 0 ? pagesWithRequiredFlags[index - 1].key : null,
      }
    },
  )

  const getAllFieldsForPage = (page: FormLayoutPage) => {
    return page.contents.flatMap((field) => {
      if (field.type === 'field') {
        return field
      }
      return field.fields
    })
  }

  const getAllFields = () => {
    const fields = pagesWithRequiredFlags.flatMap((page) =>
      getAllFieldsForPage(page),
    )

    return fields
  }

  const getAllFieldsInPage = (pageKey: string) => {
    const page = pagesWithRequiredFlags.find((page) => page.key === pageKey)
    if (!page) {
      throw new Error(`Page with key ${pageKey} not found`)
    }
    return getAllFieldsForPage(page).map((field) => field.key)
  }

  const getSchema = (): typeof ElevatorSurveyPartialSchema => {
    return ElevatorSurveySchema.partial().required({
      ...Object.fromEntries([...requiredKeys].map((key) => [key, true])),
      // Literal key required so the returned schema type narrows to
      // `typeof ElevatorSurveyPartialSchema`; redundant at runtime since
      // `elevatorType` is in ALWAYS_REQUIRED_SURVEY_FIELDS.
      elevatorType: true,
    })
  }

  const layout: ElevatorSurveyFormLayout = {
    firstStep: stepsWithForwardBackwardLinks[0],
    lastStep:
      stepsWithForwardBackwardLinks[stepsWithForwardBackwardLinks.length - 1],
    steps: stepsWithForwardBackwardLinks,
    byKey: Object.fromEntries(
      stepsWithForwardBackwardLinks.map((step) => [step.key, step]),
    ),
    getAllFields,
    getAllFieldsInPage,
    getSchema,
  }

  return layout
}

export const layoutFields = allFieldsToFormLayoutFields(AllElevatorSurveyFields)
